import "./header.scss";
import { AccountArea } from "@components/account";
import { NetworkStatusBar } from "@components/network-status-bar";
import { FC, memo, useMemo } from "react";
import { useTitle } from "@contexts/title.context";
import { BrandIcon, MenuIcon } from "@components/icons";
import { AWSUser } from "@archetypes/auth";
import { Button } from "@components/button";
import { useFavicon } from "@hooks/use-favicon";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";
import { AppData } from "@archetypes/app";
import { AppMenuSchemaType } from "@dialogs/app-add-edit-dialog/app-add-edit-form.schema";
import { useIsMinimumTablet } from "@hooks/use-media-query";
import classNames from "classnames";
import { WorkSpaceEnum } from "@layouts/admin-layout";

interface HeaderProps {
  basePath: string;
  user?: AWSUser;
  appId?: string;
  onHeaderButtonClick?: () => void;
  isAppView?: boolean;
  appData?: AppData;
  menuData?: AppMenuSchemaType[];
  fontColor?: string;
  primaryColor?: string;
  headerClassName?: string;
  onHamburgerClick?: () => void;
  tabsArray?: string[];
}

const HeaderComponent: FC<HeaderProps> = ({
  basePath,
  user,
  onHeaderButtonClick,
  isAppView,
  appData,
  fontColor = "#000",
  headerClassName,
  onHamburgerClick,
  tabsArray,
}) => {
  const { title } = useTitle();
  const { appId } = useParams();
  const { pathname } = useLocation();
  const baseRoute = useMemo(() => {
    return "/builder/apps/" + appId;
  }, [appId]);
  const appMetaData = useMemo(() => {
    if (isAppView && appData) {
      return {
        titlePrefix: appData.appName,
        appLogo: appData.appLogo,
        faviconLogo: appData.favicon,
      };
    }
    return undefined;
  }, [appData, isAppView]);

  const header = useMemo(() => {
    if (!appMetaData?.titlePrefix) {
      return title;
    }
    if (title) {
      return appMetaData?.titlePrefix + " > " + title;
    }
    return appMetaData?.titlePrefix ?? "";
  }, [title, appMetaData]);

  useFavicon(appMetaData?.faviconLogo);

  const isMobile = !useIsMinimumTablet();

  return (
    <>
      <header className={classNames("app-header", { "top-menu-container": tabsArray?.length })}>
        <div className="header-main">
          <div className={`flex align-center ${!tabsArray?.length ? "w-100-104" : ""}`}>
            <div>
              {isAppView && isMobile && (
                <Button
                  className={classNames("toggle-menu", headerClassName)}
                  onClick={() => onHamburgerClick?.()}
                  variant="icon"
                  style={{ background: fontColor }}
                >
                  <MenuIcon />
                </Button>
              )}

              {isAppView ? (
                <Button className="logo only-desktop" onClick={onHeaderButtonClick}>
                  {appMetaData?.appLogo ? (
                    <img src={appMetaData.appLogo} alt="applogo" decoding="async" loading="eager" />
                  ) : (
                    <BrandIcon height="35" width="35" />
                  )}
                </Button>
              ) : (
                <Link to={basePath === "/apps" ? "/apps" : "/builder/apps"} className="logo">
                  <BrandIcon height="35" width="35" />
                </Link>
              )}
            </div>

            <h1 className="page-title text-black">{header}</h1>
          </div>

          {tabsArray && tabsArray?.length > 0 && (
            <ul className="top-menu">
              {tabsArray?.map((tab) => {
                const route = tab === WorkSpaceEnum.ManageForm ? "/forms" : "/dashboards";
                const isActive = pathname.endsWith(route);
                return (
                  <li key={tab} className={`text-black ${isActive ? "active" : ""}`}>
                    <NavLink to={`${baseRoute}${route}`}>{tab}</NavLink>
                  </li>
                );
              })}
            </ul>
          )}

          {user && (
            <div className="account-area">
              <AccountArea basePath={basePath} />
            </div>
          )}
        </div>
      </header>
      <NetworkStatusBar />
    </>
  );
};

export const Header = memo(HeaderComponent);
